import React, { useState, useEffect } from 'react';
import Typography from '@/components/ui/typography';
import OverviewCard from './OverviewCard';
import { useHospital } from '@/hooks/useHospital';
import EstimateSavingIcon from '@/assets/Svg/estimateSavingSvg.svg';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store/store';
import { fetchOverviewByCheckType } from '@/store/rulesSlice';
import { userService } from '@/services/userService';
import { MaiaFeatureFlag } from '@/types/api';

const isFlagEnabled = (
  flags: MaiaFeatureFlag[] | null,
  key: string,
): boolean => (flags ? flags.some((f) => f.key === key && f.enabled) : true);

const Dashboard: React.FC = () => {
  const {
    userVerificationLoading,
    userVerificationError,
    getSelectedHospitalData,
  } = useHospital();
  const dispatch = useDispatch<AppDispatch>();
  const { overviewLoading, overviewError, overviewByCheckType } = useSelector(
    (state: RootState) => state.rules,
  );

  const [featureFlags, setFeatureFlags] = useState<MaiaFeatureFlag[] | null>(
    null,
  );
  const [featureFlagsLoading, setFeatureFlagsLoading] = useState(false);

  useEffect(() => {
    const tenantId = getSelectedHospitalData()?.tenantId;
    if (!tenantId) return;

    dispatch(fetchOverviewByCheckType(tenantId));
  }, [dispatch, getSelectedHospitalData]);

  // Fetch feature flags so we can conditionally render overview cards.
  useEffect(() => {
    const tenantId = getSelectedHospitalData()?.tenantId;
    if (!tenantId) {
      setFeatureFlags(null);
      setFeatureFlagsLoading(false);
      return;
    }

    let cancelled = false;
    setFeatureFlagsLoading(true);

    userService
      .getFeatureFlags(tenantId)
      .then((flags) => {
        if (!cancelled) setFeatureFlags(flags);
      })
      .catch(() => {
        if (!cancelled) setFeatureFlags(null);
      })
      .finally(() => {
        if (!cancelled) setFeatureFlagsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getSelectedHospitalData]);

  // Show error state when user verification fails
  if (userVerificationError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <Typography
              variant="small"
              color="destructive"
              weight="normal"
              size="14"
              className="mt-2"
            >
              Redirecting to login...
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state when user verification is in progress
  if (userVerificationLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <Typography variant="p" color="neutral700" weight="medium" size="16">
            Verifying your access...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Overview Section */}
      <section>
        <Typography
          variant="p"
          color="neutral900"
          weight="bold"
          size="20"
          className="mb-6"
        >
          Overview
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {overviewLoading || featureFlagsLoading ? (
            // Loading state for overview cards
            <>
              <div className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-7 h-7 bg-gray-300 rounded"></div>
                  <div className="w-24 h-4 bg-gray-300 rounded"></div>
                </div>
                <div className="w-16 h-10 bg-gray-300 rounded"></div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-7 h-7 bg-gray-300 rounded"></div>
                  <div className="w-24 h-4 bg-gray-300 rounded"></div>
                </div>
                <div className="w-16 h-10 bg-gray-300 rounded"></div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-7 h-7 bg-gray-300 rounded"></div>
                  <div className="w-24 h-4 bg-gray-300 rounded"></div>
                </div>
                <div className="w-16 h-10 bg-gray-300 rounded"></div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-7 h-7 bg-gray-300 rounded"></div>
                  <div className="w-24 h-4 bg-gray-300 rounded"></div>
                </div>
                <div className="w-16 h-10 bg-gray-300 rounded"></div>
              </div>
            </>
          ) : overviewError ? (
            // Error state for overview cards
            <div className="col-span-5 bg-red-50 border border-red-200 rounded-lg p-4">
              <Typography
                variant="p"
                color="destructive"
                weight="medium"
                size="14"
              >
                Error loading overview: {overviewError}
              </Typography>
            </div>
          ) : (
            // Overview cards with API data
            (() => {
              const historyOverview = overviewByCheckType?.PATIENT_HISTORY;
              const denialAppealOverview = overviewByCheckType?.DENIAL_APPEAL;
              const emOverview = overviewByCheckType?.ENM_LEVEL;
              const enableEnmLevelCard = isFlagEnabled(
                featureFlags,
                'ENM_LEVEL',
              );
              const enableAutoCoderCard = isFlagEnabled(
                featureFlags,
                'AUTO_CODER',
              );
              const enableDenialAppealCard = isFlagEnabled(
                featureFlags,
                'DENIAL_APPEAL',
              );
              const emAutoCoderPercentChange =
                overviewByCheckType?.ENM_AUTOCODER?.percentChange || 0;
              const surgicalAutoCoderPercentChange =
                overviewByCheckType?.SURGICAL_AUTOCODER?.percentChange || 0;
              const enmAutocoderChecks =
                overviewByCheckType?.ENM_AUTOCODER?.totalChecks || 0;
              const surgicalAutocoderChecks =
                overviewByCheckType?.SURGICAL_AUTOCODER?.totalChecks || 0;
              const totalAutocodeChecks =
                enmAutocoderChecks + surgicalAutocoderChecks;

              return (
                <>
                  <OverviewCard
                    title="Total Historical Checks This Month"
                    value={historyOverview?.totalChecks?.toString() || '0'}
                    icon={
                      <img
                        src={EstimateSavingIcon}
                        alt="History Check"
                        className="w-7 h-7"
                      />
                    }
                    percentageChange={
                      historyOverview?.percentChange !== null &&
                      historyOverview?.percentChange !== undefined
                        ? {
                            value: historyOverview.percentChange,
                            isPositive: historyOverview.percentChange > 0,
                          }
                        : undefined
                    }
                  />
                  {enableEnmLevelCard && (
                    <OverviewCard
                      title="Total E/M Checks This Month"
                      value={emOverview?.totalChecks?.toString() || '0'}
                      icon={
                        <img
                          src={EstimateSavingIcon}
                          alt="E/M Checks"
                          className="w-7 h-7"
                        />
                      }
                      percentageChange={
                        emOverview?.percentChange !== null &&
                        emOverview?.percentChange !== undefined
                          ? {
                              value: emOverview.percentChange,
                              isPositive: emOverview.percentChange > 0,
                            }
                          : undefined
                      }
                    />
                  )}
                  {enableAutoCoderCard && (
                    <OverviewCard
                      title=" Total Autocode Checks This Month"
                      value={totalAutocodeChecks.toString()}
                      percentageChange={
                        emAutoCoderPercentChange >
                        surgicalAutoCoderPercentChange
                          ? {
                              value:
                                emAutoCoderPercentChange -
                                surgicalAutoCoderPercentChange,
                              isPositive: true,
                            }
                          : {
                              value:
                                surgicalAutoCoderPercentChange -
                                emAutoCoderPercentChange,
                              isPositive: false,
                            }
                      }
                      icon={
                        <img
                          src={EstimateSavingIcon}
                          alt="Autocode Checks"
                          className="w-7 h-7"
                        />
                      }
                    />
                  )}
                  {enableDenialAppealCard && (
                    <OverviewCard
                      title="Total Denial Appeal This Month"
                      value={
                        denialAppealOverview?.totalChecks?.toString() || '0'
                      }
                      icon={
                        <img
                          src={EstimateSavingIcon}
                          alt="Denial Appeal"
                          className="w-7 h-7"
                        />
                      }
                      percentageChange={
                        denialAppealOverview?.percentChange !== null &&
                        denialAppealOverview?.percentChange !== undefined
                          ? {
                              value: denialAppealOverview.percentChange,
                              isPositive:
                                denialAppealOverview.percentChange > 0,
                            }
                          : undefined
                      }
                    />
                  )}
                </>
              );
            })()
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
